import { useEffect, useRef, useState } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { Box, CircularProgress, Alert, IconButton, Typography } from "@mui/material";
import FlipCameraAndroidIcon from "@mui/icons-material/FlipCameraAndroid";

/**
 * Hints para ZXing:
 * - POSSIBLE_FORMATS: formatos comunes en etiquetas de depósito (Code128, Code39, EAN, QR)
 * - TRY_HARDER: escaneo más agresivo — detecta barcodes en ángulo, distancia o poca luz
 */
const hints = new Map();
hints.set(DecodeHintType.POSSIBLE_FORMATS, [
  BarcodeFormat.CODE_128,   // El más común en etiquetas de depósito
  BarcodeFormat.CODE_39,
  BarcodeFormat.EAN_13,
  BarcodeFormat.EAN_8,
  BarcodeFormat.CODABAR,
  BarcodeFormat.QR_CODE,
]);
hints.set(DecodeHintType.TRY_HARDER, true);

/**
 * BarcodeScanner — accede a la cámara y decodifica códigos de barras en tiempo real.
 *
 * Props:
 *   active    {boolean}   — true = cámara encendida, false = detenida
 *   onDetected {Function} — callback(code: string) cuando detecta un código
 */
export const BarcodeScanner = ({ active, onDetected }) => {
  const videoRef = useRef(null);
  const readerRef = useRef(null);
  const controlsRef = useRef(null);

  const [cameras, setCameras] = useState([]);
  const [cameraIdx, setCameraIdx] = useState(0);
  const [status, setStatus] = useState("init"); // "init" | "scanning" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!active) {
      controlsRef.current?.stop();
      return;
    }

    // Crear reader con hints configurados
    const reader = new BrowserMultiFormatReader(hints);
    readerRef.current = reader;

    BrowserMultiFormatReader.listVideoInputDevices()
      .then((devices) => {
        if (!devices.length) {
          setStatus("error");
          setErrorMsg("No se encontró cámara disponible en este dispositivo.");
          return;
        }
        setCameras(devices);
        // Preferir cámara trasera (environment): buscar por label, si no hay, usar la última
        const backIdx = devices.findIndex((d) =>
          d.label.toLowerCase().includes("back") ||
          d.label.toLowerCase().includes("trasera") ||
          d.label.toLowerCase().includes("environment") ||
          d.label.toLowerCase().includes("rear")
        );
        const startIdx = backIdx !== -1 ? backIdx : devices.length > 1 ? devices.length - 1 : 0;
        setCameraIdx(startIdx);
        startScanning(reader, devices[startIdx]?.deviceId);
      })
      .catch((err) => {
        setStatus("error");
        setErrorMsg(
          err.name === "NotAllowedError"
            ? "Permiso de cámara denegado. Habilitá el acceso desde la configuración del navegador."
            : "No se pudo acceder a la cámara: " + err.message
        );
      });

    return () => {
      controlsRef.current?.stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  const startScanning = async (reader, deviceId) => {
    setStatus("scanning");
    try {
      // Constraints: pedir la resolución más alta disponible para mejor detección
      const constraints = deviceId
        ? { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 } }
        : { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } };

      const controls = await reader.decodeFromConstraints(
        { video: constraints },
        videoRef.current,
        (result, err, controls) => { // eslint-disable-line no-unused-vars
          if (result) {
            onDetected(result.getText());
          }
          // NotFoundException ocurre en cada frame sin código — es normal, no loguear
        }
      );
      controlsRef.current = controls;
    } catch (err) {
      setStatus("error");
      setErrorMsg("Error al iniciar la cámara: " + err.message);
    }
  };

  const flipCamera = () => {
    if (cameras.length < 2) return;
    controlsRef.current?.stop();
    const nextIdx = (cameraIdx + 1) % cameras.length;
    setCameraIdx(nextIdx);
    startScanning(readerRef.current, cameras[nextIdx]?.deviceId);
  };

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        bgcolor: "#000",
        borderRadius: 2,
        overflow: "hidden",
        minHeight: 220,
      }}
    >
      {/* Video stream — playsInline y muted son necesarios para autoplay en mobile */}
      <video
        ref={videoRef}
        playsInline
        muted
        style={{ width: "100%", display: "block", maxHeight: "60vh", objectFit: "cover" }}
      />

      {/* Frame guía rectangular */}
      {status === "scanning" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              width: 260,
              height: 110,
              border: "3px solid #fff",
              borderRadius: 1,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.40)",
              position: "relative",
              // Esquinas destacadas en azul
              "&::before": {
                content: '""',
                position: "absolute",
                top: -3, left: -3,
                width: 22, height: 22,
                borderTop: "4px solid #90caf9",
                borderLeft: "4px solid #90caf9",
              },
              "&::after": {
                content: '""',
                position: "absolute",
                bottom: -3, right: -3,
                width: 22, height: 22,
                borderBottom: "4px solid #90caf9",
                borderRight: "4px solid #90caf9",
              },
            }}
          />
        </Box>
      )}

      {/* Línea de escaneo animada */}
      {status === "scanning" && (
        <Box
          sx={{
            position: "absolute",
            left: "calc(50% - 130px)",
            width: 260,
            height: 2,
            bgcolor: "#f44336",
            boxShadow: "0 0 8px 2px rgba(244,67,54,0.7)",
            animation: "scanLine 2s ease-in-out infinite",
            "@keyframes scanLine": {
              "0%":   { top: "calc(50% - 52px)" },
              "50%":  { top: "calc(50% + 52px)" },
              "100%": { top: "calc(50% - 52px)" },
            },
          }}
        />
      )}

      {/* Botón flip cámara */}
      {status === "scanning" && cameras.length > 1 && (
        <IconButton
          onClick={flipCamera}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "rgba(0,0,0,0.55)",
            color: "#fff",
            "&:hover": { bgcolor: "rgba(0,0,0,0.80)" },
          }}
        >
          <FlipCameraAndroidIcon />
        </IconButton>
      )}

      {/* Estado: iniciando */}
      {status === "init" && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress sx={{ color: "#90caf9" }} />
          <Typography color="rgba(255,255,255,0.7)" variant="body2">
            Iniciando cámara...
          </Typography>
        </Box>
      )}

      {/* Estado: error */}
      {status === "error" && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{errorMsg}</Alert>
        </Box>
      )}
    </Box>
  );
};
